import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { getRandomId } from '../../../../../../../common/utils/utils';
import { annotationMetadataAtom } from '../../../../../../labelling-v2/atoms/annotation-atoms';
import type { KeyframeStepperSessionT, MlTrackingJobT, MlTrackingKeyframeT } from '../../../../../../labelling-v2/atoms/ml-tracking-atoms';
import { keyframeStepperSessionAtom } from '../../../../../../labelling-v2/atoms/ml-tracking-atoms';
import { isFrameInAnySpan } from '../../../../../../labelling-v2/utils/frame-span-utils';
import type { AnnotationType, BoundingBoxT } from '../../../../../types/annotations.type';
import { MAX_TRACKING_FRAMES } from '../constants';
import { computeKeyframePositions } from '../keyframe-positions';
type UseKeyframeStepperArgsT = {
  currentFrame: number;
  // Initial end of the range; once a session exists the persisted `session.endFrame` wins.
  endFrame?: number;
  maxFrames: number;
  annotationId: string;
  annotationType: AnnotationType;
  displayName: string;
  dispatchSetCurrentFrame: (frame: number) => void;
  step?: number;
};
type UseKeyframeStepperResultT = {
  positions: number[];
  chips: number[];
  stepIndex: number;
  // Persisted range for the session — pinned to the frame the stepper opened on / the user's End edit.
  startFrame: number;
  endFrame: number;
  setEndFrame: (endFrame: number) => void;
  isLastKeyframe: boolean;
  canTrack: boolean;
  goToNext: (currentBox: BoundingBoxT | null) => number | null;
  buildJob: (currentBox: BoundingBoxT | null) => MlTrackingJobT | null;
  endSession: () => void;
};

/**
 * Drives the keyframe-adjust stepper for one rectangle object. Session state lives in
 * `keyframeStepperSessionAtom` (keyed by annotationId) so it survives the popover closing and
 * re-opening on each step; boxes are snapshotted incrementally into the job via goToNext/buildJob.
 */
export const useKeyframeStepper = ({
  currentFrame,
  endFrame,
  maxFrames,
  annotationId,
  annotationType,
  displayName,
  dispatchSetCurrentFrame,
  step = MAX_TRACKING_FRAMES
}: UseKeyframeStepperArgsT): UseKeyframeStepperResultT => {
  const [session, setSession] = useAtom(keyframeStepperSessionAtom);
  const annotationMetadata = useAtomValue(annotationMetadataAtom);

  // The persisted session only applies to the object being stepped; otherwise start fresh.
  const isCurrentSession = session !== null && session.annotationId === annotationId;
  const startFrame = isCurrentSession ? session.startFrame : currentFrame;
  const stepIndex = isCurrentSession ? session.stepIndex : 0;

  // End persists in the session once created; before that the passed `endFrame` (or the last frame)
  // is the default. This is what keeps a user's End edit alive across the popover remounting on Next.
  const initialEnd = endFrame ?? maxFrames - 1;
  const effectiveEndFrame = isCurrentSession ? session.endFrame : initialEnd;
  const clampedEnd = Math.min(effectiveEndFrame, maxFrames - 1);
  const positions = useMemo(() => computeKeyframePositions({
    startFrame,
    endFrame: clampedEnd,
    step
  }), [startFrame, clampedEnd, step]);
  const chips = useMemo(() => positions.slice(1), [positions]);
  const isLastKeyframe = stepIndex >= positions.length - 1;
  const canTrack = clampedEnd > startFrame;

  // Returns the session owning this annotation, seeding a fresh one when none exists.
  const baseSessionFor = useCallback((prev: KeyframeStepperSessionT | null): KeyframeStepperSessionT => prev !== null && prev.annotationId === annotationId ? prev : {
    annotationId,
    startFrame,
    endFrame: effectiveEndFrame,
    stepIndex: 0,
    keyFrames: new Map<number, BoundingBoxT>()
  }, [annotationId, startFrame, effectiveEndFrame]);

  // Persists a user's End edit into the session (creating it if needed), so it survives the remount.
  const setEndFrame = useCallback((nextEnd: number) => {
    setSession(prev => ({
      ...baseSessionFor(prev),
      endFrame: nextEnd
    }));
  }, [setSession, baseSessionFor]);
  const goToNext = useCallback((currentBox: BoundingBoxT | null): number | null => {
    const frameNo = positions[stepIndex];
    const nextFrame = positions[stepIndex + 1];
    setSession(prev => {
      const base = baseSessionFor(prev);
      const keyFrames = new Map(base.keyFrames);
      if (frameNo !== undefined && currentBox) {
        keyFrames.set(frameNo, currentBox);
      }
      // Advance only when there is a next keyframe to move to.
      return {
        ...base,
        stepIndex: nextFrame === undefined ? base.stepIndex : base.stepIndex + 1,
        keyFrames
      };
    });
    if (nextFrame === undefined) {
      return null;
    }
    dispatchSetCurrentFrame(nextFrame);
    return nextFrame;
  }, [positions, stepIndex, setSession, baseSessionFor, dispatchSetCurrentFrame]);
  const buildJob = useCallback((currentBox: BoundingBoxT | null): MlTrackingJobT | null => {
    const frameNo = positions[stepIndex];
    const keyFramesMap = new Map<number, BoundingBoxT>(isCurrentSession ? session.keyFrames : []);
    if (frameNo !== undefined && currentBox) {
      keyFramesMap.set(frameNo, currentBox);
    }

    // Drop keyframes whose frame no longer belongs to the annotation (deleted mid-session)
    const spans = annotationMetadata.get(annotationId)?.spans ?? [];
    const keyFrames: MlTrackingKeyframeT[] = Array.from(keyFramesMap.entries()).filter(([frame]) => isFrameInAnySpan(frame, spans)).sort(([frameA], [frameB]) => frameA - frameB).map(([frameNo, boundingBox]) => ({
      frameNo,
      boundingBox
    }));
    if (keyFrames.length === 0) {
      return null;
    }
    return {
      id: getRandomId(),
      annotationId,
      displayName,
      annotationType,
      keyFrames,
      startFrame,
      endFrame: clampedEnd
    };
  }, [positions, stepIndex, isCurrentSession, session, annotationId, displayName, annotationType, startFrame, clampedEnd, annotationMetadata]);

  // Clears the persisted session (called after the job is enqueued on the last Next).
  const endSession = useCallback(() => setSession(null), [setSession]);
  return {
    positions,
    chips,
    stepIndex,
    startFrame,
    endFrame: effectiveEndFrame,
    setEndFrame,
    isLastKeyframe,
    canTrack,
    goToNext,
    buildJob,
    endSession
  };
};